import React from 'react';
import { Input, InputProps, InputRef, Select, Space, Typography } from 'antd';
import {
  defaultCountries as origDefaultCountries,
  FlagImage,
  usePhoneInput,
} from 'react-international-phone';

import { useCompany } from '../../shell';

import './phoneInput.scss';

/**
 * This component is based on example:
 * https://github.com/goveo/react-international-phone/blob/master/src/stories/UiLibsExample/components/AntPhone.tsx
 */

export interface PhoneInputProps
  extends Omit<InputProps, 'value' | 'onChange'> {
  defaultCountry?: string;
  value?: string;
  onChange?: (phone: string) => void;
}

const EU_COUNTRIES = new Set([
  'al',
  'ad',
  'at',
  'by',
  'be',
  'ba',
  'bg',
  'hr',
  'cz',
  'dk',
  'ee',
  'fo',
  'fi',
  'fr',
  'de',
  'gi',
  'gr',
  'va',
  'hu',
  'is',
  'ie',
  'it',
  'lv',
  'li',
  'lt',
  'lu',
  'mk',
  'mt',
  'md',
  'mc',
  'me',
  'nl',
  'no',
  'pl',
  'pt',
  'ro',
  'ru',
  'sm',
  'rs',
  'sk',
  'si',
  'es',
  'se',
  'ch',
  'ua',
  'gb',
]);

const defaultCountries = origDefaultCountries.filter(([, iso2]) =>
  EU_COUNTRIES.has(iso2),
);

export const PhoneInput: React.FC<PhoneInputProps> = ({
  value,
  onChange,
  defaultCountry,
  ...inputProps
}) => {
  const company = useCompany();
  const phoneInput = usePhoneInput({
    defaultCountry: defaultCountry || company.country.toLowerCase(),
    value,
    onChange: (data) => {
      if (onChange) {
        onChange(
          data.phone === `+${data.country.dialCode}`
            ? // handle "+380" as an empty input too
              ''
            : data.phone,
        );
      }
    },
  });

  const inputRef = React.useRef<InputRef>(null);

  // Need to reassign inputRef because antd provides not default ref
  React.useEffect(() => {
    if (phoneInput.inputRef) {
      phoneInput.inputRef.current = inputRef.current?.input || null;
    }
  }, [inputRef.current?.input, phoneInput.inputRef]);

  return (
    <Input
      type="tel"
      autoComplete="tel"
      addonBefore={
        <Select
          showSearch
          optionFilterProp="labelForFilter"
          popupMatchSelectWidth={false}
          value={phoneInput.country.iso2}
          onChange={(newIso2) => phoneInput.setCountry(newIso2)}
          options={defaultCountries.map(([name, iso2, dialCode]) => ({
            label: <FlagImage iso2={iso2} />,
            dropdownLabel: (
              <Space>
                <FlagImage iso2={iso2} />
                <span>{name}</span>
                <Typography.Text type="secondary">+{dialCode}</Typography.Text>
              </Space>
            ),
            labelForFilter: `${iso2} ${name} ${dialCode}`,
            value: iso2,
          }))}
          optionRender={(option) => option.data.dropdownLabel}
        />
      }
      {...inputProps}
      value={phoneInput.inputValue}
      onChange={(event) => {
        // a hotfix for https://github.com/react-component/input/issues/62
        // rc-input creates a copy of input element instead of passing original
        // DOM node. Which makes impossible to control cursor position
        const currentTarget = inputRef.current!.input;

        event = Object.create(event, {
          target: { value: currentTarget },
          currentTarget: { value: currentTarget },
        });

        phoneInput.handlePhoneValueChange(event);
      }}
      ref={inputRef}
    />
  );
};

/*
// field implementation example. May be move into component in future
const { _ } = useLingui();

<Form.Item
  name="point"
  label={<Trans id="settings.appeal.sources.phone.number">Phone number</Trans>}
  required
  normalize={(value) => value.replace(/^\+/, '')}
  validateTrigger="onBlur"
  rules={[
    {
      required: true,
    },
    {
      async validator(_noop, value) {
        const isValid = await intlTel.isValid(`+${value}`);

        if (!isValid) {
          throw new Error(
            _(
              msg({
                id: 'general.invalid.phone.number',
                message: 'Invalid phone number',
              }),
            ),
          );
        }
      },
    },
  ]}
>
  <PhoneInput />
</Form.Item>;
*/
